import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, CheckCircle2, Building2, LogOut } from "lucide-react";

import * as api from "../api";
import { useAuth } from "../auth";

const STATUS_STYLES = {
  DRAFT: "bg-amber-100 text-amber-900",
  VERIFIED: "bg-emerald-100 text-emerald-800",
  RETIRED: "bg-slate-100 text-slate-500",
};

export default function BackofficeKBPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // {id?, question, answer, tags, status}

  async function load() {
    try { setRows(await api.kbList()); } catch { setRows([]); }
  }
  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing({ question: "", answer: "", tags: "", status: "DRAFT" });
  }
  function openEdit(r) {
    setEditing({ id: r.id, question: r.question, answer: r.answer,
                 tags: (r.tags || []).join(", "), status: r.status });
  }

  async function save() {
    const payload = {
      question: editing.question.trim(),
      answer: editing.answer.trim(),
      tags: editing.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: editing.status,
    };
    if (!payload.question || !payload.answer) {
      toast.error("Question and answer required");
      return;
    }
    try {
      if (editing.id) {
        await api.kbUpdate(editing.id, payload);
        toast.success("Entry updated");
      } else {
        await api.kbCreate(payload);
        toast.success("Entry created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  }

  async function verify(r) {
    try {
      await api.kbUpdate(r.id, { status: "VERIFIED" });
      toast.success("Marked verified");
      await load();
    } catch (e) { toast.error("Could not verify"); }
  }

  async function del(r) {
    if (!window.confirm(`Delete: "${r.question}"?`)) return;
    try { await api.kbDelete(r.id); toast.success("Deleted"); await load(); }
    catch (e) { toast.error("Could not delete"); }
  }

  const filtered = (rows || []).filter((r) =>
    !filter || r.question.toLowerCase().includes(filter.toLowerCase())
    || (r.tags || []).some((t) => t.toLowerCase().includes(filter.toLowerCase())));

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-slate-900">
            <Building2 size={22} className="text-[#C5283F]" />
            <span className="font-display text-xl font-semibold">Infin · Back office</span>
          </Link>
          <div className="flex items-center gap-3 text-xs">
            <Link to="/admin/escalations" className="text-slate-600 hover:text-slate-900">Escalations</Link>
            <Link to="/admin" className="text-slate-600 hover:text-slate-900">Cases</Link>
            <span className="text-slate-500">{user?.email}</span>
            <button onClick={() => { logout(); nav("/"); }}
                    className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Knowledge base</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Only VERIFIED entries are surfaced to founders by the AI agent.
            </p>
          </div>
          <button onClick={openNew} data-testid="kb-new-btn"
                  className="bg-[#C5283F] text-white px-4 py-2.5 rounded-lg inline-flex items-center gap-2 hover:bg-[#A82035]">
            <Plus size={14} /> New entry
          </button>
        </div>

        <input
          value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by question or tag…"
          data-testid="kb-filter"
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm mb-4 bg-white"
        />

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {rows === null && <div className="p-8 text-sm text-slate-500">Loading…</div>}
          {rows && rows.length === 0 && (
            <div className="p-12 text-center text-slate-500 text-sm">No entries yet.</div>
          )}
          {filtered.map((r) => (
            <div key={r.id}
                 data-testid={`kb-row-${r.id}`}
                 className="px-6 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={[
                      "text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full",
                      STATUS_STYLES[r.status],
                    ].join(" ")} data-testid={`kb-status-${r.id}`}>{r.status}</span>
                    {(r.tags || []).map((t) => (
                      <span key={t} className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                  <div className="text-sm font-medium text-slate-900 mb-1">{r.question}</div>
                  <div className="text-sm text-slate-600 line-clamp-2">{r.answer}</div>
                  {r.source && (
                    <div className="text-[11px] text-slate-400 mt-1 font-mono">source: {r.source}</div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {r.status !== "VERIFIED" && (
                    <button onClick={() => verify(r)} title="Mark verified"
                            data-testid={`kb-verify-${r.id}`}
                            className="p-2 text-slate-400 hover:text-emerald-700">
                      <CheckCircle2 size={15} />
                    </button>
                  )}
                  <button onClick={() => openEdit(r)} title="Edit"
                          data-testid={`kb-edit-${r.id}`}
                          className="p-2 text-slate-400 hover:text-slate-700">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => del(r)} title="Delete"
                          data-testid={`kb-delete-${r.id}`}
                          className="p-2 text-slate-400 hover:text-red-700">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl"
               onClick={(e) => e.stopPropagation()}
               data-testid="kb-edit-modal">
            <div className="font-display text-lg font-semibold mb-4">
              {editing.id ? "Edit entry" : "New entry"}
            </div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Question</label>
            <input value={editing.question}
                   onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                   data-testid="kb-edit-question"
                   className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3" />
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Verified answer</label>
            <textarea value={editing.answer} rows={6}
                      onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                      data-testid="kb-edit-answer"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3" />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Tags (comma-separated)</label>
                <input value={editing.tags}
                       onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                       data-testid="kb-edit-tags"
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Status</label>
                <select value={editing.status}
                        onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                        data-testid="kb-edit-status"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  <option value="DRAFT">DRAFT</option>
                  <option value="VERIFIED">VERIFIED</option>
                  <option value="RETIRED">RETIRED</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)}
                      className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm">Cancel</button>
              <button onClick={save}
                      data-testid="kb-edit-save"
                      className="bg-[#C5283F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#A82035]">
                {editing.id ? "Save changes" : "Create entry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
